import { PRODUCT_NAME } from '@lib/constants/product'
import { cn } from '@/lib/utils'

/** Each lockup's width over its height, from the files' viewBoxes. */
const ASPECT = { full: 254.49 / 57.7, short: 170.8 / 57.7 } as const

interface Props {
  /** Rendered width in CSS pixels. The files are SVG, so any width is crisp. */
  width?: number
  /**
   * `full` is the whole lockup — the mark, FLOH and VENUES. `short` drops VENUES,
   * for anywhere narrower than 240px: below that VENUES is too small to read
   * (4.8px tall at the kit's stated 120px minimum), so a narrow lockup that keeps
   * it is a smudge where the name should be. See `/design-system/brand.html`.
   */
  lockup?: 'full' | 'short'
  /**
   * `theme` follows the page's theme; `dark` is for a ground that is dark in
   * both themes — the blackwood sidebar, the sign-in panel — and always takes
   * the reversed file.
   */
  ground?: 'theme' | 'dark'
  className?: string
}

/**
 * The FLOH Venues lockup, on whichever ground it sits on.
 *
 * Two files per lockup rather than one with a filter: the brand gives the dark
 * ground its own colors (an ivory frame and FLOH, lighter waves, a brighter
 * gold), not an inverted logo. The app's dark mode is a class on the root rather
 * than a media query, so this is a `dark:` pair rather than `<picture>`.
 *
 * SVG, because every file in this kit is outlined paths — there is no live text
 * to come out in a substitute font. Emails are the exception and take the PNG
 * (`/brand/logo-dark.png`), because no mail client draws SVG.
 */
export function BrandLockup({ width = 240, lockup = 'full', ground = 'theme', className }: Props) {
  const height = Math.round((width / ASPECT[lockup]) * 10) / 10
  const file = lockup === 'full' ? 'logo' : 'logo-short'
  const shared = { alt: PRODUCT_NAME, width, height }
  if (ground === 'dark') {
    return <img src={`/brand/${file}-dark.svg`} {...shared} className={cn('h-auto', className)} />
  }
  return (
    <>
      <img src={`/brand/${file}.svg`} {...shared} className={cn('h-auto dark:hidden', className)} />
      <img src={`/brand/${file}-dark.svg`} {...shared} className={cn('hidden h-auto dark:block', className)} />
    </>
  )
}
